import { useEffect, useCallback, useRef } from 'react';

const KEYS = {
    ARROW_DOWN: 'ArrowDown',
    ARROW_UP: 'ArrowUp',
    HOME: 'Home',
    END: 'End',
    ENTER: 'Enter',
    ESCAPE: 'Escape',
    TAB: 'Tab',
};

export const useKeyboardNavigation = (isOpen, options, highlightIndex, onSelect, onHighlight, onOpen, onClose, inputRef) => {
    const highlightIndexRef = useRef(highlightIndex);
    highlightIndexRef.current = highlightIndex;

    const handleKeyDown = useCallback(
        (e) => {
            const isComboBoxFocused = inputRef.current === document.activeElement;
            if (!isComboBoxFocused) return;

            const { key } = e;

            if (!isOpen) {
                if (key === KEYS.ENTER) {
                    e.preventDefault();
                    onOpen();
                }
                return;
            }

            const hasOptions = options.length > 0;

            switch (key) {
                case KEYS.ARROW_DOWN:
                    e.preventDefault();
                    if (hasOptions) {
                        onHighlight((current) => Math.min(options.length - 1, current + 1));
                    }
                    break;

                case KEYS.ARROW_UP:
                    e.preventDefault();
                    if (hasOptions) {
                        onHighlight((current) => Math.max(0, current - 1));
                    }
                    break;

                case KEYS.HOME:
                    e.preventDefault();
                    if (hasOptions) {
                        onHighlight(0);
                    }
                    break;

                case KEYS.END:
                    e.preventDefault();
                    if (hasOptions) {
                        onHighlight(options.length - 1);
                    }
                    break;

                case KEYS.ENTER: {
                    e.preventDefault();
                    const currentIndex = highlightIndexRef.current;
                    if (currentIndex >= 0 && currentIndex < options.length) {
                        const option = options[currentIndex];
                        if (option && !option.disabled) {
                            onSelect(option);
                        }
                    }
                    break;
                }

                case KEYS.ESCAPE:
                    e.preventDefault();
                    onClose();
                    break;

                case KEYS.TAB:
                    onClose();
                    break;

                default:
                    break;
            }
        },
        [isOpen, options, onSelect, onHighlight, onOpen, onClose, inputRef]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);
};
